import CryptoJS from 'crypto-js';
import axios from 'axios';
import { btoa, atob } from '../../lib/helpers';
import { dataIntervalUpload } from '../params';
import { postApi } from '../../lib/fetcher';

const insertTransaction = {
  namespaced: true,
  state() {
    return {
      loading: false,
      errors: [],
      flashErrorMessage: '',
      flashError: false,
      countData: 0,
      countSuccess: 0,
      countFailed: 0,
      uploadProgress: 0,
      onUpload: false,
      insertProgress: 0,
      onInsert: false,
      insertName: '',
      importId: null,
      hasFailedImport: false,
      failedImportController: '',
      failedImportDate: '',
      countDone: 0,
      countSame: 0,
      intervalImport: null,
    };
  },

  mutations: {
    setLoading(state, loading) {
      state.loading = loading;
    },
    setUploadProgress(state, uploadProgress) {
      state.uploadProgress = uploadProgress;
    },
    setOnUpload(state, onUpload) {
      state.onUpload = onUpload;
    },
    setInsertProgress(state, insertProgress) {
      state.insertProgress = insertProgress;
    },
    setInsertName(state, insertName) {
      state.insertName = insertName;
    },
    setOnInsert(state, onInsert) {
      state.onInsert = onInsert;
    },
    setIntervalImport(state, payload) {
      state.intervalImport = payload;
    },
    setImportData(state, payload) {
      state.importId = payload.import_id;
      state.countData = payload.count_data;
    },
    setStateVariable(state, { payload, variable }) {
      state[variable] = payload;
    },
    clearLastResult(state) {
      state.countData = 0;
      state.countSuccess = 0;
      state.countFailed = 0;
      state.countDone = 0;
      state.countSame = 0;
      state.uploadProgress = 0;
      state.onUpload = false;
      state.insertProgress = 0;
      state.onInsert = false;
      state.insertName = '';
      state.intervalImport = null;
      state.importId = null;
      state.hasFailedImport = false;
      state.failedImportController = '';
      state.failedImportDate = '';
    },
    setCancelation(state, payload) {
      state.requestData = payload;
    }
  },

  actions: {
    async downloadTemplate({ commit, dispatch }, switcher) {
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/transaction/bulk-insert-transaction/download`,
        responseType: 'blob',
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram')).token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data: { switcher }
      };
      try {
        const response = await axios(config);
        return response;
      } catch (error) {
        let message = 'Failed to download template. Please try again.';
        if (error.response?.data instanceof Blob) {
          try {
            const parsed = JSON.parse(await error.response.data.text());
            message =
              parsed.message || parsed.message_en || parsed.message_id || message;
          } catch (e) {
            // response body is not JSON, keep the default message
          }
        }
        if (error.response?.status === 401) {
          dispatch('logout', null, { root: true });
        } else {
          commit('setSnackbar', {
            status: true,
            message,
            icon: 'mdi-alert',
            color: 'error'
          }, { root: true });
        }
        return null;
      }
    },

    async bulkUpload(ctx, { file, date }) {
      const data = new FormData();
      data.append('file', file);
      ctx.commit('setOnUpload', true);
      ctx.commit(
        'setInsertProgressGlobal',
        {
          absoluteBar: true,
          absoluteBarText: 'Uploading...'
        },
        { root: true }
      );

      const failed = () => {
        ctx.commit('setOnUpload', false);
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: 'Upload Failed !',
            icon: 'mdi-alert',
            color: 'red'
          },
          { root: true }
        );
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: false,
            absoluteBarText: ''
          },
          { root: true }
        );
      };

      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/transaction/bulk-insert-transaction/import`,
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'X-Origin-Path': window.location.pathname
        },
        onUploadProgress(progressEvent) {
          ctx.commit(
            'setUploadProgress',
            parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100),
              10
            )
          );
        },
        data
      };
      try {
        return await axios(config)
          .then((response) => {
            if (response.data.code == '200') {
              ctx.commit('setImportData', response.data.data);
              const interval = setInterval(() => {
                ctx.dispatch('importStatus', {
                  date,
                  controller: 'bulk-insert-transaction',
                });
              }, dataIntervalUpload);
              ctx.commit('setIntervalImport', interval);
              ctx.commit('setInsertName', 'Start to insert data..');
              ctx.commit('setOnInsert', true);
              ctx.commit(
                'setInsertProgressGlobal',
                {
                  absoluteBar: true,
                  absoluteBarText: 'Start to insert data..',
                },
                { root: true }
              );
            } else {
              failed();
              if (response.data.code == '302') {
                ctx.commit(
                  'setSnackbar',
                  {
                    status: true,
                    message: response.data.data.message,
                    icon: 'mdi-alert',
                    color: 'red',
                  },
                  { root: true }
                );
              }
            }
            return response.data;
          })
          .catch((error) => {
            failed();
            if (error.response.status == '403') {
              ctx.commit(
                'setSnackbar',
                {
                  status: true,
                  message: error.response.data.message,
                  icon: 'mdi-alert',
                  color: 'red',
                },
                { root: true }
              );
            } else if (error.response.status == '401') {
              ctx.dispatch('logout', null, { root: true });
            }
            return null;
          })
          .finally(() => {
            ctx.commit('setOnUpload', false);
          });
      } catch (error) {
        failed();
        return false;
      }
    },

    async importStatus(ctx, { date, controller }) {
      const data = JSON.stringify({
        import_id: ctx.state.importId,
        count_data: ctx.state.countData,
        version: btoa(
          CryptoJS.SHA256(
            ctx.state.importId.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        )
      });

      const setCountToDefault = () => {
        ctx.commit('setStateVariable', {
          payload: 0,
          variable: 'countDone'
        });
        ctx.commit('setStateVariable', {
          payload: 0,
          variable: 'countSame'
        });
      };

      const failed = () => {
        clearInterval(ctx.state.intervalImport);
        ctx.commit('setOnInsert', false);
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: 'Upload Failed !',
            icon: 'mdi-alert',
            color: 'red'
          },
          { root: true }
        );
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: false,
            absoluteBarText: ''
          },
          { root: true }
        );
        setCountToDefault();
      };

      try {
        const urlApi = `/index.php?r=api/v1/transaction/${controller}/status-import`;

        const responseData = await postApi(ctx, urlApi, data);

        if (responseData.code == '00') {
          clearInterval(ctx.state.intervalImport);
          ctx.commit('setOnInsert', false);
          ctx.commit('setStateVariable', {
            variable: 'countSuccess',
            payload: responseData.data.count_success
          });
          ctx.commit('setStateVariable', {
            variable: 'countFailed',
            payload: responseData.data.count_failed
          });
          if (responseData.data.count_success != ctx.state.countData) {
            ctx.commit('setStateVariable', {
              variable: 'failedImportController',
              payload: controller
            });
            ctx.commit('setStateVariable', {
              variable: 'failedImportDate',
              payload: date
            });
            ctx.commit('setStateVariable', {
              variable: 'hasFailedImport',
              payload: true
            });
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: 'Upload done with some failed record !',
                icon: 'mdi-alert',
                color: 'orange'
              },
              { root: true }
            );
          } else {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message: 'Success, Bulk Upload Done !',
                icon: 'mdi-alert',
                color: 'green'
              },
              { root: true }
            );
          }
          setCountToDefault();
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: false,
              absoluteBarText: ''
            },
            { root: true }
          );
        } else if (responseData.code == '302') {
          failed();
        } else {
          const totalDataDone =
            parseInt(responseData.data.count_success, 10) +
            parseInt(responseData.data.count_failed, 10);
          ctx.commit(
            'setInsertName',
            `Insert ${totalDataDone}/${ctx.state.countData} data..`
          );
          ctx.commit(
            'setInsertProgress',
            (totalDataDone / ctx.state.countData) * 100
          );
          ctx.commit(
            'setInsertProgressGlobal',
            {
              absoluteBar: true,
              absoluteBarText: `Insert ${totalDataDone}/${ctx.state.countData} data..`
            },
            { root: true }
          );
          if (ctx.state.countDone != totalDataDone) {
            ctx.commit('setStateVariable', {
              payload: totalDataDone,
              variable: 'countDone'
            });
            ctx.commit('setStateVariable', {
              payload: 0,
              variable: 'countSame'
            });
          } else {
            ctx.commit('setStateVariable', {
              payload: ctx.state.countSame + 1,
              variable: 'countSame'
            });
          }
          if (ctx.state.countSame > 2) {
            failed();
          }
        }
      } catch (error) {
        failed();
      }
    },

    async downloadFailedImport(ctx) {
      const date = ctx.state.failedImportDate;
      const controller = ctx.state.failedImportController;
      const data = JSON.stringify({
        import_id: ctx.state.importId,
        version: btoa(
          CryptoJS.SHA256(
            ctx.state.importId.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        )
      });
      const config = {
        method: 'post',
        url: `${process.env.VUE_APP_API_BASE_URL}/index.php?r=api/v1/transaction/${controller}/download-failed-import`,
        responseType: 'blob',
        headers: {
          'X-CSRF-TOKEN': JSON.parse(localStorage.getItem('logged_in_qram'))
            .token,
          'Content-Type': 'application/json',
          'X-Origin-Path': window.location.pathname
        },
        data
      };
      return axios(config)
        .then(response => response)
        .catch(async error => {
          let message = 'Failed to download failed records. Please try again.';
          if (error.response?.data instanceof Blob) {
            try {
              const parsed = JSON.parse(await error.response.data.text());
              message =
                parsed.message || parsed.message_en || parsed.message_id || message;
            } catch (e) {
              // response body is not JSON, keep the default message
            }
          }
          if (error.response?.status === 401) {
            ctx.dispatch('logout', null, { root: true });
          } else {
            ctx.commit(
              'setSnackbar',
              {
                status: true,
                message,
                icon: 'mdi-alert',
                color: 'red'
              },
              { root: true }
            );
          }
          return null;
        });
    }
  }
};
export default insertTransaction;
